import { useCallback, useEffect, useState } from "react";
import { Button as AntButton, Col, Modal, Popconfirm, Row, Typography, Upload } from "antd";
import { FileExcelOutlined, UnorderedListOutlined } from "@ant-design/icons";
import Card from "../../card/Card";
import { useTheme } from "../../../contexts/ThemeContext";
import { Toast } from "../../ui";
import SynchronizationService from "../../../services/synchronizationService";
import HowItWorks from "./HowItWorks";
import UploadArea from "./UploadArea";
import UploadResult from "./UploadResult";
import CurrentSheet from "./CurrentSheet";
import EntriesDrawer from "./EntriesDrawer";
import {
  MAX_FILE_MB,
  TEMPLATE_FILE_NAME,
  blobErrorMessage,
  getErpCapabilities,
  notificationMessage,
  plural,
} from "./additionalInfoUtils";

const { Text } = Typography;

/**
 * Seção "Informações adicionais" da configuração do sync: instruções, modelo, envio, planilha
 * atual e acesso aos lançamentos. Cada envio substitui todos os lançamentos do anterior. Quem
 * decide se a seção aparece (sync padrão de ERP suportado) é o SynchronizationSettings; a regra
 * de verdade está no Backend.
 */
const AdditionalInfoSection = ({ syncId, erp, canEdit = false }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const capabilities = getErpCapabilities(erp, summary);
  const { supportsSales, supportsDepartments } = capabilities;

  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Regras abertas no primeiro acesso (nenhuma planilha ainda); depois, como o usuário deixar.
  const [rulesOpen, setRulesOpen] = useState(null);

  // Só o resumo (contagens e planilhas): os lançamentos são carregados pelo Drawer.
  const load = useCallback(async () => {
    if (!syncId) return;
    setLoading(true);
    try {
      const data = await SynchronizationService.getInfoAdicional(syncId, { page: 1, pageSize: 1 });
      setSummary(data || null);
    } catch {
      // O interceptor já exibe o erro.
    } finally {
      setLoading(false);
    }
  }, [syncId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (rulesOpen === null && summary) setRulesOpen((summary.qtdTitulos ?? 0) + (summary.qtdVendas ?? 0) === 0);
  }, [summary, rulesOpen]);

  const refresh = () => {
    load();
    setRefreshKey((k) => k + 1);
  };

  // Cada envio substitui o anterior: no máximo uma planilha ativa (a mais recente, se houver resto antigo).
  const loteAtual = (summary?.lotes || [])[0] || null;
  const qtdTitulos = summary?.qtdTitulos ?? 0;
  const qtdVendas = summary?.qtdVendas ?? 0;
  const temLancamentos = qtdTitulos + qtdVendas > 0;
  const busy = uploading || clearing;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const blob = await SynchronizationService.downloadInfoAdicionalTemplate(syncId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = TEMPLATE_FILE_NAME;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      Toast.error((await blobErrorMessage(error)) || "Erro ao baixar o modelo.");
    } finally {
      setDownloading(false);
    }
  };

  const doUpload = async (file) => {
    setUploading(true);
    setUploadPercent(0);
    setResult(null);
    try {
      const res = await SynchronizationService.uploadInfoAdicional(syncId, file, (event) => {
        if (event.total) setUploadPercent(Math.round((event.loaded * 100) / event.total));
      });
      setResult({ ...res, erros: [], avisos: res?.avisos || [] });
      Toast.success("Planilha enviada.");
      refresh();
    } catch (error) {
      const data = error?.response?.data;
      if (Array.isArray(data?.erros) && data.erros.length > 0) {
        setResult({ erros: data.erros, avisos: data.avisos || [] });
      } else if (notificationMessage(data)) {
        Toast.error(notificationMessage(data));
      } else if (error?.response?.status === 403) {
        Toast.error("Você não tem permissão para enviar informações adicionais desta empresa.");
      }
      // Demais casos (500, rede) o interceptor já exibiu.
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file) => {
    if (!file.name?.toLowerCase().endsWith(".xlsx")) {
      Toast.error("Envie a planilha no formato .xlsx (use o modelo).");
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      Toast.error(`O arquivo passa do limite de ${MAX_FILE_MB} MB.`);
      return Upload.LIST_IGNORE;
    }
    if (temLancamentos) {
      const atuais = [plural(qtdTitulos, "título", "títulos")];
      if (supportsSales) atuais.push(plural(qtdVendas, "venda", "vendas"));
      Modal.confirm({
        title: "Substituir a planilha atual?",
        content: `Esta planilha substituirá todos os lançamentos atuais (${atuais.join(" e ")}). Continuar?`,
        okText: "Substituir",
        cancelText: "Cancelar",
        onOk: () => doUpload(file),
      });
    } else {
      doUpload(file);
    }
    return false;
  };

  const handleClear = async () => {
    try {
      setClearing(true);
      await SynchronizationService.clearInfoAdicional(syncId);
      Toast.success("Lançamentos removidos.");
      setResult(null);
      refresh();
    } catch {
      // O interceptor já exibe o erro.
    } finally {
      setClearing(false);
    }
  };

  const label = { fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: theme.textSecondary };

  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FileExcelOutlined style={{ fontSize: 16, color: theme.primary }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>Informações adicionais</span>
        </div>
      }
      style={{ marginBottom: 24 }}
    >
      <Text style={{ display: "block", marginBottom: 20, fontSize: 14, color: theme.textSecondary }}>
        {`Lance títulos${supportsSales ? " e vendas" : ""} que não estão no ERP para incluí-los nos relatórios.`}
      </Text>

      <Row gutter={[32, 24]}>
        <Col xs={24} md={13}>
          <div style={{ ...label, marginBottom: 12 }}>Como funciona</div>
          <HowItWorks
            capabilities={capabilities}
            rulesOpen={Boolean(rulesOpen)}
            onToggleRules={() => setRulesOpen((open) => !open)}
          />
        </Col>
        <Col xs={24} md={11}>
          <div style={{ ...label, marginBottom: 12 }}>Enviar</div>
          <UploadArea
            canEdit={canEdit}
            downloading={downloading}
            uploading={uploading}
            uploadPercent={uploadPercent}
            disabled={busy}
            onDownload={handleDownload}
            beforeUpload={beforeUpload}
          />
        </Col>
      </Row>

      {result && (
        <div style={{ marginTop: 20 }}>
          <UploadResult result={result} supportsSales={supportsSales} onClose={() => setResult(null)} />
        </div>
      )}

      <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${theme.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <span style={label}>Planilha atual</span>
          </div>
          {temLancamentos && (
            <AntButton type="text" size="small" icon={<UnorderedListOutlined />} onClick={() => setDrawerOpen(true)}>
              Ver lançamentos
            </AntButton>
          )}
          {canEdit && temLancamentos && (
            <Popconfirm
              title="Remover todos os lançamentos?"
              description="Os lançamentos saem dos relatórios após a carga incremental."
              okText="Remover tudo"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={handleClear}
              disabled={busy}
            >
              <AntButton type="text" size="small" danger loading={clearing} disabled={busy && !clearing}>
                Limpar tudo
              </AntButton>
            </Popconfirm>
          )}
        </div>

        <CurrentSheet lote={loteAtual} loading={loading} supportsSales={supportsSales} />
      </div>

      <EntriesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        syncId={syncId}
        supportsSales={supportsSales}
        supportsDepartments={supportsDepartments}
        refreshKey={refreshKey}
      />
    </Card>
  );
};

export default AdditionalInfoSection;
